import { CbhIcon, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { type ReactNode } from "react";

import { Button } from "./Button";

type IconType = "map-pin" | "clock" | "break";

type ActivityIcon =
  | {
      type: "icon";
      iconType: IconType;
    }
  | {
      type: "image";
      src: string;
      alt?: string;
    };

export interface ActivityItem {
  id: string;
  icon: ActivityIcon;
  title: string;
  subtitle?: ReactNode | ReactNode[];
  timestamp: string;
  onEdit?: () => void;
}

interface ActivityListProps {
  activities: ActivityItem[];
}

function toSubtitleLines(subtitle: ActivityItem["subtitle"]): ReactNode[] {
  if (Array.isArray(subtitle)) {
    return subtitle;
  }

  return isDefined(subtitle) ? [subtitle] : [];
}

export function ActivityList(props: ActivityListProps) {
  const { activities } = props;

  return (
    <Stack gap={7} sx={{ position: "relative" }}>
      <Box
        sx={{
          position: "absolute",
          left: 14,
          top: 30,
          bottom: 0,
          width: 2,
          borderLeft: (theme) => `2px dashed ${theme.palette.grey[400]}`,
          zIndex: 0,
          pointerEvents: "none",
          opacity: 0.8,
        }}
      />

      {activities.map((activity) => (
        <Stack key={activity.id} direction="row" gap={3} alignItems="center" width="100%">
          <Box
            data-testid={`activity-icon-${
              activity.icon.type === "icon"
                ? `${activity.icon.iconType}-${activity.id}`
                : activity.id
            }`}
            sx={{
              width: 30,
              height: 30,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              backgroundColor: (theme) => theme.palette.common.white,
              position: "relative",
              zIndex: 1,
              overflow: "hidden",
            }}
          >
            {activity.icon.type === "icon" ? (
              <CbhIcon size="small" type={activity.icon.iconType} />
            ) : (
              <Box
                component="img"
                src={activity.icon.src}
                alt={activity.icon.alt ?? activity.title}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            )}
          </Box>

          <Stack gap={1} sx={{ flex: 1, minWidth: 0 }}>
            <Text bold variant="body2">
              {activity.title}
            </Text>
            {toSubtitleLines(activity.subtitle).map((line, index) => (
              <Text
                // oxlint-disable-next-line react/no-array-index-key -- subtitles are positionally stable
                key={index}
                variant="caption"
                data-testid={`activity-${activity.id}-subtitle-${index}`}
                sx={{ color: (theme) => theme.palette.grey[700] }}
              >
                {line}
              </Text>
            ))}
          </Stack>

          {isDefined(activity.timestamp) &&
            (isDefined(activity.onEdit) ? (
              <Button
                aria-label={`Edit ${activity.title}`}
                endIconType="pencil"
                variant="outlined"
                sx={{
                  flexShrink: 0,
                  fontSize: (theme) => theme.typography.body2.fontSize,
                  letterSpacing: (theme) => theme.typography.body2.letterSpacing,
                }}
                onClick={activity.onEdit}
              >
                {activity.timestamp}
              </Button>
            ) : (
              <Text semibold variant="body2" sx={{ flexShrink: 0 }}>
                {activity.timestamp}
              </Text>
            ))}
        </Stack>
      ))}
    </Stack>
  );
}
